import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, GripVertical, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface FAQ {
  id?: string;
  question: string;
  answer: string;
  display_order: number;
  is_active: boolean;
}

interface FAQManagerProps {
  ptaId: string;
}

const defaultFAQs: Omit<FAQ, 'id'>[] = [
  {
    question: "Do I get paid?",
    answer: "No, PTA members are volunteers. However, you'll gain valuable experience, make lasting friendships, and have the satisfaction of making a real difference in your child's school community.",
    display_order: 0,
    is_active: true
  },
  {
    question: "I'm concerned it will take up too much of my free time?",
    answer: "We understand everyone has busy lives! You can choose your level of involvement - from helping at occasional events to taking on a committee role. Even a few hours per term makes a difference.",
    display_order: 1,
    is_active: true
  },
  {
    question: "Where do you meet?",
    answer: "We typically meet in the school staff room. Meeting times are arranged to suit the majority of members, and we also offer virtual attendance options when possible.",
    display_order: 2,
    is_active: true
  },
  {
    question: "What if I just want to help out at events?",
    answer: "That's perfect! We have an 'Occasional Helper' role for parents who want to volunteer at specific events without the commitment of a committee position. Just let us know your availability.",
    display_order: 3,
    is_active: true
  }
];

export function FAQManager({ ptaId }: FAQManagerProps) {
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchFAQs();
  }, [ptaId]);

  const fetchFAQs = async () => {
    const { data, error } = await supabase
      .from('pta_faqs')
      .select('*')
      .eq('pta_id', ptaId)
      .order('display_order', { ascending: true });

    if (data && data.length > 0) {
      setFaqs(data);
    } else {
      // No FAQs yet - show empty state for user to add their own
      setFaqs([]);
    }
    setLoading(false);
  };

  const handleAddFAQ = () => {
    setFaqs([
      ...faqs,
      {
        question: "",
        answer: "",
        display_order: faqs.length,
        is_active: true
      }
    ]);
  };

  const handleRemoveFAQ = (index: number) => {
    const faq = faqs[index];
    setFaqs(faqs.filter((_, i) => i !== index));
    
    // If it has an ID, we need to delete it from the database
    if (faq.id) {
      supabase.from('pta_faqs').delete().eq('id', faq.id);
    }
  };

  const handleUpdateFAQ = (index: number, field: 'question' | 'answer', value: string) => {
    const updated = [...faqs];
    updated[index] = { ...updated[index], [field]: value };
    setFaqs(updated);
  };

  const handleLoadDefaults = async () => {
    // Delete existing FAQs first
    await supabase.from('pta_faqs').delete().eq('pta_id', ptaId);
    
    // Insert defaults
    const { data, error } = await supabase
      .from('pta_faqs')
      .insert(defaultFAQs.map(faq => ({ ...faq, pta_id: ptaId })))
      .select();

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load default FAQs",
        variant: "destructive"
      });
    } else {
      setFaqs(data || []);
      toast({
        title: "Defaults loaded",
        description: "Default FAQs have been loaded. Click Save to keep them."
      });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Delete all existing FAQs for this PTA
      await supabase.from('pta_faqs').delete().eq('pta_id', ptaId);

      // Insert all current FAQs
      if (faqs.length > 0) {
        const faqsToInsert = faqs.map((faq, index) => ({
          pta_id: ptaId,
          question: faq.question,
          answer: faq.answer,
          display_order: index,
          is_active: true
        }));

        const { error } = await supabase.from('pta_faqs').insert(faqsToInsert);
        if (error) throw error;
      }

      toast({
        title: "FAQs saved",
        description: "Your FAQ section has been updated"
      });
      fetchFAQs();
    } catch (error) {
      console.error('Error saving FAQs:', error);
      toast({
        title: "Error",
        description: "Failed to save FAQs",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4 text-muted-foreground">Loading FAQs...</div>;
  }

  return (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>FAQ Section</CardTitle>
            <CardDescription>
              Manage frequently asked questions for the "Join Our PTA" page
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleLoadDefaults}>
              Load Defaults
            </Button>
            <Button variant="outline" size="sm" onClick={handleAddFAQ}>
              <Plus className="h-4 w-4 mr-1" />
              Add FAQ
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {faqs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No FAQs configured yet.</p>
            <p className="text-sm">Click "Load Defaults" to start with common questions, or "Add FAQ" to create your own.</p>
          </div>
        ) : (
          faqs.map((faq, index) => (
            <div key={index} className="p-4 border border-border rounded-lg space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-3">
                  <div className="space-y-1">
                    <Label>Question</Label>
                    <Input
                      value={faq.question}
                      onChange={(e) => handleUpdateFAQ(index, 'question', e.target.value)}
                      placeholder="e.g., Do I get paid?"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Answer</Label>
                    <Textarea
                      value={faq.answer}
                      onChange={(e) => handleUpdateFAQ(index, 'answer', e.target.value)}
                      placeholder="Enter the answer..."
                      rows={3}
                    />
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveFAQ(index)}
                  className="shrink-0"
                >
                  <Trash2 className="h-4 w-4 text-foreground" />
                </Button>
              </div>
            </div>
          ))
        )}

        {faqs.length > 0 && (
          <Button onClick={handleSave} disabled={saving} className="w-full">
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save FAQs"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}